import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Clock, ChevronRight, Plus, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { useShiftTemplates } from '@/hooks/useShiftTemplates';
import { usePatientRole } from '@/hooks/usePatientRole';
import { formatTimeDisplay } from '@/types/shift-templates';
import { isPatientInactiveRlsError, PATIENT_INACTIVE_TOAST_MESSAGE } from '@/lib/patient-status-errors';
import { cn } from '@/lib/utils';

const NONE = 'none';

interface CaregiverShiftAssignmentProps {
  patientId: string;
  memberId: string;
  caregiverName: string;
  currentTemplateId: string | null;
  /** template id -> how many caregivers are assigned to it */
  assignmentCounts?: Record<string, number>;
  canEdit?: boolean;
  onUpdate?: () => void;
}

export function CaregiverShiftAssignment({
  patientId,
  memberId,
  caregiverName,
  currentTemplateId,
  assignmentCounts = {},
  canEdit = true,
  onUpdate,
}: CaregiverShiftAssignmentProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: templates = [], isLoading } = useShiftTemplates(patientId);
  const { isPatientActive } = usePatientRole(patientId);

  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<string>(currentTemplateId || NONE);
  const [isSaving, setIsSaving] = useState(false);

  const currentTemplate = useMemo(
    () => templates.find((tpl) => tpl.id === currentTemplateId),
    [templates, currentTemplateId]
  );

  const openSheet = () => {
    if (!isPatientActive) {
      toast.error(PATIENT_INACTIVE_TOAST_MESSAGE);
      return;
    }
    setSelected(currentTemplateId || NONE);
    setOpen(true);
  };

  const handleSave = async () => {
    const nextId = selected === NONE ? null : selected;
    if (nextId === (currentTemplateId || null)) {
      setOpen(false);
      return;
    }

    setIsSaving(true);
    try {
      const { error } = await supabase
        .from('patient_user_links')
        .update({ shift_template_id: nextId })
        .eq('id', memberId);

      if (error) throw error;

      toast.success(t('shiftTemplates.assignment.updatedFor', { name: caregiverName }));
      setOpen(false);
      onUpdate?.();
    } catch (error) {
      console.error('Error updating shift template:', error);
      if (isPatientInactiveRlsError(error)) {
        toast.error(PATIENT_INACTIVE_TOAST_MESSAGE);
      } else {
        toast.error(t('common.error'));
      }
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return <Skeleton className="h-6 w-40" />;
  }

  return (
    <>
      <div className="flex items-center gap-2 flex-wrap">
        {currentTemplate ? (
          <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
            <Clock className="w-3.5 h-3.5 shrink-0" />
            <span className="text-foreground font-medium">
              {t('shiftTemplates.assignment.currentLabel')} {currentTemplate.name}
            </span>
            <span>
              · {formatTimeDisplay(currentTemplate.default_start_time)}–{formatTimeDisplay(currentTemplate.default_end_time)}
            </span>
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-warning bg-warning/10 border border-warning/20 rounded-full px-2 py-0.5">
            <AlertCircle className="w-3.5 h-3.5 shrink-0" />
            {t('shiftTemplates.assignment.noneAssigned')}
          </span>
        )}

        {canEdit && (
          <Button
            variant={currentTemplate ? 'link' : 'default'}
            size="sm"
            className={cn(
              'h-6 px-2 text-xs',
              currentTemplate && 'h-auto p-0 underline-offset-2'
            )}
            onClick={openSheet}
            disabled={!isPatientActive}
          >
            {currentTemplate
              ? t('shiftTemplates.assignment.change')
              : t('shiftTemplates.assignment.define')}
          </Button>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('shiftTemplates.assignment.dialogTitle', { name: caregiverName })}</DialogTitle>
            <DialogDescription>
              {t('shiftTemplates.assignment.dialogDescription')}
            </DialogDescription>
          </DialogHeader>

          {templates.length === 0 ? (
            <div className="text-center py-6 space-y-3 border-2 border-dashed rounded-lg">
              <Clock className="w-8 h-8 mx-auto text-muted-foreground" />
              <p className="text-sm text-muted-foreground px-4">
                {t('shiftTemplates.assignment.emptyTitle')}
              </p>
              <Button size="sm" onClick={() => navigate(`/app/patients/${patientId}/templates/new`)}>
                <Plus className="w-4 h-4 mr-1" />
                {t('shiftTemplates.new')}
              </Button>
            </div>
          ) : (
            <RadioGroup value={selected} onValueChange={setSelected} className="space-y-2">
              {templates.map((template) => {
                const count = assignmentCounts[template.id] || 0;
                const isSelected = selected === template.id;
                return (
                  <label
                    key={template.id}
                    className={cn(
                      'flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors',
                      isSelected ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'
                    )}
                  >
                    <RadioGroupItem value={template.id} className="mt-1" />
                    <div className="flex-1 min-w-0 space-y-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm">{template.name}</span>
                        {template.is_overnight && (
                          <Badge variant="outline" className="text-[11px] font-normal">
                            {t('shiftTemplates.assignment.overnightBadge')}
                          </Badge>
                        )}
                        {template.has_variable_hours && (
                          <Badge variant="outline" className="text-[11px] font-normal">
                            {t('shiftTemplates.assignment.variableBadge')}
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatTimeDisplay(template.default_start_time)}–{formatTimeDisplay(template.default_end_time)}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {t('shiftTemplates.assignment.caregiverCount', { count })}
                      </p>
                    </div>
                  </label>
                );
              })}

              <label
                className={cn(
                  'flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors',
                  selected === NONE ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'
                )}
              >
                <RadioGroupItem value={NONE} className="mt-1" />
                <div className="flex-1 min-w-0 space-y-1">
                  <span className="font-medium text-sm">{t('shiftTemplates.assignment.noFixedShift')}</span>
                  <p className="text-xs text-muted-foreground">
                    {t('shiftTemplates.assignment.noFixedShiftHint')}
                  </p>
                </div>
              </label>
            </RadioGroup>
          )}

          {templates.length > 0 && (
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setOpen(false)} disabled={isSaving}>
                {t('common.cancel')}
              </Button>
              <Button onClick={handleSave} disabled={isSaving}>
                {isSaving ? t('shiftTemplates.saving') : t('shiftTemplates.assignment.save')}
              </Button>
            </DialogFooter>
          )}

          {templates.length > 0 && (
            <button
              type="button"
              className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mx-auto"
              onClick={() => navigate(`/app/patients/${patientId}/templates/new`)}
            >
              {t('shiftTemplates.assignment.manageShifts')}
              <ChevronRight className="w-3 h-3" />
            </button>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
